// src/components/common/WelcomeToast.jsx
//
// Purpose-specific toast shown once after lean-onboarding completion. Points
// at the docking station's profile-assistant icon (the FIRST button in the
// stack, not the visual center of the column) so the user knows where to go
// for future profile edits. NOT a general-purpose toast — for those, use
// FloatingToast.
//
// Positioning model:
//   - Caller passes `targetRef` (a React ref attached to the profile-editor
//     button in dockingstation.jsx). On mount + window resize, the toast
//     measures the button's getBoundingClientRect() and positions itself
//     just-left-of-button with its arrow vertically aligned to the button's
//     center. This is robust against future docking-station layout changes
//     (button order swaps, padding tweaks, additional rows) without needing
//     a CSS-pixel math update each time.
//   - Fallback (no ref or button unmounted): centers vertically on viewport
//     and positions at right:110px. Preserves the old behavior for
//     defensive resilience.
//
// Trigger pattern (see ProfileAssistant.jsx + dockingstation.jsx):
//   - ProfileAssistant.handleNameSubmission dispatches 'eh:show-welcome-toast'
//     and writes a localStorage flag.
//   - DockingStation's event listener triggers immediately; mount-effect
//     handles the page-reload fallback.
//   - One-shot: cleared after first display.

import React, { useEffect, useState } from 'react';

export default function WelcomeToast({ message, onDismiss, autoDismissMs = 10000, targetRef }) {
  // Position state — recomputed on mount + window resize. Falls back to the
  // viewport-center if the targetRef isn't available (defensive).
  const [pos, setPos] = useState({ top: '50%', useCenter: true });

  useEffect(() => {
    const recompute = () => {
      const el = targetRef?.current;
      if (!el || typeof el.getBoundingClientRect !== 'function') {
        setPos({ top: '50%', useCenter: true });
        return;
      }
      const rect = el.getBoundingClientRect();
      // Vertical center of the target button, in viewport pixels. We anchor
      // the toast's arrow at this y-coordinate by setting toast top = this
      // value and using transform: translateY(-50%) so the toast box is
      // centered on the same point.
      const buttonCenterY = rect.top + (rect.height / 2);
      if (Number.isFinite(buttonCenterY) && buttonCenterY > 0) {
        setPos({ top: `${buttonCenterY}px`, useCenter: false });
      } else {
        setPos({ top: '50%', useCenter: true });
      }
    };

    // Initial compute (microtask-defer in case the docking station hasn't
    // fully laid out yet on first render — rect would be 0/0/0/0).
    const raf = requestAnimationFrame(recompute);

    window.addEventListener('resize', recompute);
    window.addEventListener('scroll', recompute, { passive: true });
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', recompute);
      window.removeEventListener('scroll', recompute);
    };
  }, [targetRef]);

  useEffect(() => {
    if (!autoDismissMs) return;
    const t = setTimeout(() => onDismiss?.(), autoDismissMs);
    return () => clearTimeout(t);
  }, [autoDismissMs, onDismiss]);

  return (
    <>
      {/* Desktop / wide-viewport: positioned at the profile-editor button's
          vertical center, with a right-pointing arrow at the toast's
          vertical center (= the button's vertical center, by construction). */}
      <div
        className="welcome-toast-desktop"
        style={{
          position: 'fixed',
          right: '110px',
          top: pos.top,
          transform: 'translateY(-50%)',
          width: '300px',
          maxWidth: 'calc(100vw - 140px)',
          background: '#00aef7',
          color: 'white',
          padding: '16px 36px 16px 20px',
          borderRadius: '12px',
          boxShadow: '0 6px 24px rgba(0, 0, 0, 0.3)',
          zIndex: 1050,
          animation: 'fadeIn 0.3s ease-out',
          fontSize: '14px',
          lineHeight: '1.45',
        }}
        role="status"
        aria-live="polite"
      >
        <button
          onClick={onDismiss}
          aria-label="Dismiss"
          style={{
            position: 'absolute',
            top: '6px',
            right: '10px',
            background: 'transparent',
            border: 'none',
            color: 'rgba(255, 255, 255, 0.85)',
            fontSize: '20px',
            lineHeight: '1',
            cursor: 'pointer',
            padding: '4px',
          }}
        >
          ×
        </button>
        <div style={{ paddingRight: '4px' }}>{message}</div>
        {/* Right-pointing speech-bubble arrow toward the docking station */}
        <div
          style={{
            position: 'absolute',
            right: '-10px',
            top: '50%',
            transform: 'translateY(-50%)',
            width: 0,
            height: 0,
            borderTop: '10px solid transparent',
            borderBottom: '10px solid transparent',
            borderLeft: '10px solid #00aef7',
          }}
          aria-hidden="true"
        />
      </div>

      {/* Inline media-query rule: hide desktop variant, show centered
          bottom variant on narrow viewports. Inline keyframes too so the
          component is self-contained (no global CSS dependency). */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(-50%) translateX(10px); }
          to   { opacity: 1; transform: translateY(-50%) translateX(0); }
        }
        @media (max-width: 768px) {
          .welcome-toast-desktop {
            right: 50% !important;
            top: auto !important;
            bottom: 90px !important;
            transform: translateX(50%) !important;
            width: calc(100vw - 32px) !important;
            max-width: 360px !important;
          }
          .welcome-toast-desktop > div:last-child {
            display: none !important;
          }
        }
      `}</style>
    </>
  );
}
